export default function JavaVEditPrivacy() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Privacy Policy - Java Editor &amp; Code Runner
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Effective date: <time dateTime="2026-04-22">April 22, 2026</time>
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>Cosmovex</strong> built the{" "}
            <strong>Java Editor &amp; Code Runner</strong> (JavaVEdit) app as
            an ad-supported application. This Privacy Policy explains what
            information JavaVEdit may process, how that information is used,
            and the choices you have when using the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Information We Process
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Java source files and project files:</strong> code
              files you write, open, or import are compiled and run locally
              on your device using the embedded Java runtime. They are never
              uploaded to our servers.
            </li>
            <li>
              <strong>Installed libraries:</strong> libraries you install
              through the in-app Libraries manager are downloaded from the
              Maven Central package index and stored locally on your device.
            </li>
            <li>
              <strong>Imported JAR files:</strong> JAR files you add to your
              compile classpath stay on your device. They are used only to
              compile and run your code locally.
            </li>
            <li>
              <strong>Program output:</strong> standard output, error
              messages, and responses from the local web server mode are
              displayed locally and are never transmitted to our servers.
            </li>
            <li>
              <strong>App preferences:</strong> your theme choice, editor
              settings, and preferences are stored locally on your device.
            </li>
            <li>
              <strong>Usage and ad data:</strong> if ads are enabled, AdMob
              or related Google Mobile Ads services may collect device and
              advertising data as described in Google&apos;s own privacy
              documentation.
            </li>
            <li>
              <strong>Analytics and diagnostics:</strong> Firebase Analytics
              and Firebase Crashlytics may collect app stability, usage
              patterns, and crash reports to improve reliability and user
              experience.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            How We Use Information
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              To let you write, compile, and run Java code directly on your
              device using the embedded Java runtime.
            </li>
            <li>
              To display program output, compiler errors, and stack traces
              inline.
            </li>
            <li>
              To let you install, import, and manage Java libraries at
              runtime.
            </li>
            <li>
              To host the local web server mode so your{" "}
              <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
                WebApp
              </code>{" "}
              code can be opened in your device&apos;s browser.
            </li>
            <li>
              To remember your projects, editor settings, and preferences.
            </li>
            <li>
              To improve app performance, reliability, and product quality.
            </li>
            <li>
              To display ads in supported versions of the app, where
              applicable.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Local Storage</h2>
          <p>
            JavaVEdit is designed to keep your data on your device. Java
            source files, project files, installed libraries, imported JARs,
            notebook files, and preferences are stored locally and are not
            uploaded to any server. Data remains on your device until you
            remove it, clear the app&apos;s storage, or uninstall the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Network Access During Library Installation
          </h2>
          <p>
            When you install a library via the in-app Libraries manager,
            JavaVEdit connects to the internet to download the library from
            the Maven Central package index. Only the group and artifact
            coordinates and version are sent as part of a standard HTTPS
            download request. No personal data, source code, or file data is
            transmitted during this process.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Local Web Server Mode
          </h2>
          <p>
            When you run a Java class that extends{" "}
            <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
              WebApp
            </code>
            , the app starts a small HTTP server bound to your device&apos;s
            local address so you can open your app in a browser on the same
            device. The server runs only while the mode is active and is
            stopped when you tap Stop or leave the screen. It does not
            expose your files or preferences to the network.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Advertising</h2>
          <p>
            JavaVEdit may use <strong>Google AdMob</strong> to show banner,
            interstitial, rewarded, and app-open ad formats. AdMob may
            collect and process identifiers, device information, approximate
            location, and ad interaction data to deliver and measure ads.
            You can remove ads via an in-app purchase.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            In-App Purchases
          </h2>
          <p>
            JavaVEdit offers an optional in-app purchase to remove
            advertisements and unlock Pro themes. Purchases are processed
            through Google Play and are subject to Google&apos;s terms and
            privacy policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Third-Party Services
          </h2>
          <p>JavaVEdit may rely on third-party services such as:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Google Play Services</li>
            <li>Google AdMob</li>
            <li>Firebase Analytics</li>
            <li>Firebase Crashlytics</li>
            <li>Firebase Remote Config</li>
            <li>
              Maven Central package index (for on-demand library downloads
              initiated by you)
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Sharing</h2>
          <p>
            We do not sell your source code, project files, or personal
            data. Data may be shared only with service providers required to
            operate app features, such as advertising, analytics, app
            distribution, or diagnostics infrastructure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Retention</h2>
          <p>
            Local app data remains on your device until you remove it, clear
            the app&apos;s storage, or uninstall the app. Data collected by
            third-party services such as AdMob and Firebase is retained
            according to those providers&apos; own policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Children&apos;s Privacy
          </h2>
          <p>
            JavaVEdit is not intended for children under 13, and we do not
            knowingly collect personal information from children through the
            app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Security</h2>
          <p>
            We take reasonable steps to protect data processed by the app,
            but no method of storage or transmission is completely secure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Your Choices</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              You can delete individual source files and projects from
              inside the app.
            </li>
            <li>
              You can remove libraries and imported JARs you no longer need
              via the Libraries manager.
            </li>
            <li>
              You can uninstall the app to remove all locally stored data
              from your device.
            </li>
            <li>You can purchase ad-free to disable all advertising.</li>
            <li>
              You can manage ad personalization and advertising controls
              through your device and Google settings.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. When we do,
            we will post the updated version on this page and revise the
            effective date.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Us</h2>
          <p>
            For privacy-related questions about Java Editor &amp; Code
            Runner, contact{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
